// Marketing Creatives API Service

// Enums for categories and creative types
export const MARKETING_CATEGORIES = {
  DAILY_DEALS: 'DAILY DEALS',
  PRODUCT: 'PRODUCT',
  PROMOTION: 'PROMOTION',
  FESTIVES: 'FESTIVES',
  UPCOMING_WEBINAR: 'UPCOMING WEBINAR',
  WEEKLY_HIGHLIGHTS: 'WEEKLY HIGHLIGHTS',
};

export const CREATIVE_TYPES = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  WEBINAR: 'WEBINAR',
};

/**
 * Fetch marketing creatives by category
 * @param {string} category - Category from MARKETING_CATEGORIES enum
 * @returns {Promise<Object>} API response
 */
export const fetchMarketingCreativesByCategory = async (
  category,
  sort = 'dateOfPublication:desc',
) => {
  try {
    // Add sort param to the API call
    const url = `${
      process.env.REACT_APP_STRAPI_API_ENDPOINT
    }/all-partner-marketing-creatives?populate=*&pLevel=4&filters[category][$eq]=${category}&sort[1]=${encodeURIComponent(
      sort,
    )}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_STRAPI_API_TOKEN}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // The API returns {data: [...], meta: {...}} structure
    const result = { data: data.data || [] };

    return result;
  } catch (error) {
    console.error(`Error fetching marketing creatives for category ${category}:`, error);
    throw error;
  }
};

/**
 * Fetch all marketing creatives (all categories)
 * @returns {Promise<Object>} Combined data from all categories
 */
export const fetchAllMarketingCreatives = async () => {
  try {
    const categories = Object.values(MARKETING_CATEGORIES);
    const promises = categories.map((category) =>
      fetchMarketingCreativesByCategory(category)
        .then((data) => {
          return { category, data: data.data || [] };
        })
        .catch((error) => {
          console.error(`Failed to fetch ${category}:`, error);
          return { category, data: [] };
        }),
    );

    const results = await Promise.all(promises);

    // Transform the data to match the expected format
    const transformedData = results
      .filter((result) => result.data.length > 0)
      .map((result) => ({
        sectionTitle: formatCategoryTitle(result.category),
        category: result.category,
        list: result.data.map(transformCreativeItem),
      }));

    return transformedData;
  } catch (error) {
    console.error('Error fetching all marketing creatives:', error);
    throw error;
  }
};

/**
 * Format date for display
 * @param {string} dateString - Date string from API
 * @returns {string} Formatted date
 */
const formatDate = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; // Return original string if parsing fails
  }
};

/**
 * Transform content array from API to HTML string
 * @param {Array} content - Content array from API
 * @returns {string} HTML string
 */
const transformContent = (content) => {
  if (!Array.isArray(content)) return '';

  return content
    .map((block) => {
      switch (block.type) {
        case 'paragraph':
          const paragraphText =
            block.children
              ?.map((child) => {
                if (child.bold) {
                  return `<strong>${child.text}</strong>`;
                }
                return child.text;
              })
              .join('') || '';
          return `<p>${paragraphText}</p>`;

        case 'list':
          const listItems =
            block.children
              ?.map((item) => {
                const itemText = item.children?.map((child) => child.text).join('') || '';
                return `<li>${itemText}</li>`;
              })
              .join('') || '';
          return block.format === 'unordered' ? `<ul>${listItems}</ul>` : `<ol>${listItems}</ol>`;

        default:
          return '';
      }
    })
    .join('');
};

/**
 * Transform API creative item to match component expected format
 * @param {Object} item - Raw API item
 * @returns {Object} Transformed item
 */
export const transformCreativeItem = (item) => {
  return {
    id: item.id,
    documentId: item.documentId, // Add documentId here
    title: item.title || '',
    description: transformContent(item.content) || '',
    datePosted: formatDate(item.dateOfPublication || item.createdAt),
    tags: extractTags(item),
    views: item.viewCount || '0',
    image: extractImageUrl(item),
    creativeType: item.type || CREATIVE_TYPES.IMAGE,
    category: item.category,
    sessionDetails: {
      youtubeVideoUrl: item.youtubeVideoUrl,
      webinarUrl: item.webinarUrl,
      webinarStartTime: item.webinarStartTime,
      webinarEndTime: item.webinarEndTime,
    },
    contact: null,
    actions: [],
  };
};

/**
 * Extract tags from item
 * @param {Object} item - API item
 * @returns {Array} Array of tag objects
 */
const extractTags = (item) => {
  const tags = [];

  // Check if item was created recently (within newTagValidityInHours)
  if (item.newTagValidityInHours && item.createdAt) {
    const createdDate = new Date(item.createdAt);
    const now = new Date();
    const validityHours = Number(item.newTagValidityInHours?.replace('T', '')) || 0;
    const hoursElapsed = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60);

    if (hoursElapsed <= validityHours) {
      tags.push({ title: 'NEW', color: 'green' });
    }
  }

  // Check for webinar-related items
  if (item.type === 'WEBINAR' || item.webinarUrl) {
    // Check if webinar is live (between start and end time)
    if (item.webinarStartTime && item.webinarEndTime) {
      const now = new Date();
      const startTime = new Date(item.webinarStartTime);
      const endTime = new Date(item.webinarEndTime);

      if (now >= startTime && now <= endTime) {
        tags.push({ title: 'LIVE', color: 'red' });
      }
    }
  }

  return tags;
};

/**
 * Extract image URL from item
 * @param {Object} item - API item
 * @returns {string} Image URL
 */
const extractImageUrl = (item) => {
  // Check for direct thumbnail URL
  if (item.thumbnail?.url) {
    return item.thumbnail.url;
  }

  // Check for thumbnail medium format
  if (item.thumbnail?.formats?.medium?.url) {
    return item.thumbnail.formats.medium.url;
  }

  // Check for thumbnail small format
  if (item.thumbnail?.formats?.small?.url) {
    return item.thumbnail.formats.small.url;
  }

  // Check for thumbnail with formats
  if (item.thumbnail?.formats?.large?.url) {
    return item.thumbnail.formats.large.url;
  }

  return null;
};

/**
 * Format category title for display
 * @param {string} category - Category enum value
 * @returns {string} Formatted title
 */
const formatCategoryTitle = (category) => {
  const titleMap = {
    [MARKETING_CATEGORIES.DAILY_DEALS]: 'Daily Deals',
    [MARKETING_CATEGORIES.PRODUCT]: 'Product',
    [MARKETING_CATEGORIES.PROMOTION]: 'Promotion',
    [MARKETING_CATEGORIES.FESTIVES]: 'Festives',
    [MARKETING_CATEGORIES.UPCOMING_WEBINAR]: 'Upcoming Webinar',
    [MARKETING_CATEGORIES.WEEKLY_HIGHLIGHTS]: 'Weekly Highlights',
  };

  return (
    titleMap[category] ||
    category
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase())
  );
};
